/**
 * Create Parent Account Page
 * Create a new parent account and link to student
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useNotification } from '@/hooks/useNotification';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { createParentAccount } from '@/api/parents';
import { isValidEmail } from '@/utils/validation';
import { sanitizeUserRegistrationData } from '@/utils/inputSanitizer';
import { getUserFriendlyError } from '@/utils/errorMessages';

export const CreateParentAccountPage = () => {
  const { studentId } = useParams<{ studentId: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    parentName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.parentName.trim()) {
      newErrors.parentName = 'Parent name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/[A-Z]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter';
    } else if (!/[a-z]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one lowercase letter';
    } else if (!/[0-9]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one number';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !studentId) {
      return;
    }

    try {
      setIsCreating(true);
      
      // Sanitize form data
      const sanitized = sanitizeUserRegistrationData({
        email: formData.email,
        password: formData.password,
        firstName: formData.parentName.split(' ')[0] || formData.parentName,
        lastName: formData.parentName.split(' ').slice(1).join(' ') || '',
        schoolCode: ''
      });

      const response = await createParentAccount({
        parentName: formData.parentName.trim(),
        email: sanitized.email,
        password: sanitized.password,
        studentId,
      });

      if (response.success) {
        showNotification('success', 'Parent account created successfully');
        navigate(`/students/${studentId}/parents`);
      } else {
        showNotification('error', getUserFriendlyError(response, 'Parent account'));
      }
    } catch (error: any) {
      console.error('Failed to create parent account:', error);
      showNotification('error', getUserFriendlyError(error, 'Parent account'));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Create Parent Account</h1>
          <p className="text-slate-600 mt-2">
            Add a new parent account for this student
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate(`/students/${studentId}/parents`)}
        >
          ← Cancel
        </Button>
      </div>

      {/* Create Form */}
      <Card title="Parent Information">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Parent Name *
            </label>
            <input
              type="text"
              value={formData.parentName}
              onChange={(e) => {
                setFormData({ ...formData, parentName: e.target.value });
                setErrors({ ...errors, parentName: '' });
              }}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                errors.parentName ? 'border-red-500' : 'border-slate-300'
              }`}
              placeholder="John Doe"
              required
            />
            {errors.parentName && (
              <p className="text-sm text-red-600 mt-1">{errors.parentName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value });
                setErrors({ ...errors, email: '' });
              }}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                errors.email ? 'border-red-500' : 'border-slate-300'
              }`}
              placeholder="parent@example.com"
              required
            />
            {errors.email && (
              <p className="text-sm text-red-600 mt-1">{errors.email}</p>
            )}
            <p className="text-sm text-slate-500 mt-1">
              Parent will use this email to log in
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Password *
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => {
                setFormData({ ...formData, password: e.target.value });
                setErrors({ ...errors, password: '' });
              }}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                errors.password ? 'border-red-500' : 'border-slate-300'
              }`}
              placeholder="••••••••"
              required
            />
            {errors.password && (
              <p className="text-sm text-red-600 mt-1">{errors.password}</p>
            )}
            <p className="text-sm text-slate-500 mt-1">
              Must be at least 8 characters with uppercase, lowercase, and number
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Confirm Password *
            </label>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => {
                setFormData({ ...formData, confirmPassword: e.target.value });
                setErrors({ ...errors, confirmPassword: '' });
              }}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                errors.confirmPassword ? 'border-red-500' : 'border-slate-300'
              }`}
              placeholder="••••••••"
              required
            />
            {errors.confirmPassword && (
              <p className="text-sm text-red-600 mt-1">{errors.confirmPassword}</p>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              variant="primary"
              disabled={isCreating}
              className="flex-1"
            >
              {isCreating ? 'Creating...' : 'Create Parent Account'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/students/${studentId}/parents`)}
              disabled={isCreating}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>

      {/* Info Card */}
      <Card>
        <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">
            ℹ️ Parent Account Information
          </h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• The parent will receive login credentials via email</li>
            <li>• Parents can view their child's stories and progress</li>
            <li>• Parents cannot modify student settings or information</li>
            <li>• Multiple parents can be linked to one student</li>
          </ul>
        </div>
      </Card>
    </div>
  );
};
